import {
  Step,
  Stepper,
  type TeamMemberForJobAccess,
  useSaveHirerTeamMemberJobAccessAndNotify,
  type SaveJobAccessAndNotifyTrackingData,
  HirerTeamMemberJobAccessForm,
  type StepActionResult,
  type OnNotifyChangeEventData,
} from '@seek/hirer-team-member-job-access';
import { useTranslations } from '@vocab/react';
import { Box, TextLink } from 'braid-design-system';
import { useState } from 'react';

import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import {
  EVENT_NAME_MANAGE_NOTIFICATIONS_TEAM_ACCESS_CANCEL_PRESSED,
  EVENT_NAME_MANAGE_NOTIFICATIONS_TEAM_ACCESS_NEXT_PRESSED,
  EVENT_NAME_MANAGE_NOTIFICATIONS_TEAM_ACCESS_SAVE_PRESSED,
  EVENT_NAME_MANAGE_NOTIFICATIONS_TEAM_ACCESS_BACK_PRESSED,
  EVENT_TEAM_EMAIL_SAVE,
} from 'src/constants/pageTrackingData';
import { trackEvent } from 'src/modules/events';
import { isANZSite } from 'src/utils/site';

import {
  getAnzCsPhoneNumberFromSite,
  getHelpCentreUrlFromSite,
} from '../../../../../utils/getCsContactMethodMessageFromSite.ts';
import { AddTeamMembersCallout } from '../AddTeamMembersCallout';
import { TeamMemberEmail } from '../TeamMemberEmail';

import translations from './.vocab';

interface Props {
  onComplete: () => void;
  onCancel: () => void;
  teamMembers: TeamMemberForJobAccess[];
  jobTitle?: string;
  jobId?: string;
  userName?: string;
}
export const TeamMemberAccessStepper = ({
  onComplete,
  onCancel,
  teamMembers,
  jobTitle,
  jobId,
  userName,
}: Props) => {
  const { t } = useTranslations(translations);
  const { site, language } = useAppConfig();

  const [notifyTeam, setNotifyTeam] = useState<boolean>(false);
  const saveHirerTeamMemberJobAccessAndNotify =
    useSaveHirerTeamMemberJobAccessAndNotify();

  const trackSaveEvent = (data: SaveJobAccessAndNotifyTrackingData) =>
    trackEvent(
      notifyTeam
        ? EVENT_TEAM_EMAIL_SAVE
        : EVENT_NAME_MANAGE_NOTIFICATIONS_TEAM_ACCESS_SAVE_PRESSED,
      data,
    );

  const csPhoneNumber = getAnzCsPhoneNumberFromSite(site);

  const teamAccessErrorMessage = isANZSite(site)
    ? t('Contact CS', {
        TelLink: () => (
          <TextLink href={`tel:${csPhoneNumber}`}>{csPhoneNumber}</TextLink>
        ),
      })
    : t('Contact CS - with Customer Service link', {
        Link: (children) => (
          <TextLink href={getHelpCentreUrlFromSite(site, language)}>
            {children}
          </TextLink>
        ),
      });

  const handleSaveHirerTeamMemberJobAccessAndNotify =
    async (): Promise<StepActionResult> => {
      const saveResult =
        await saveHirerTeamMemberJobAccessAndNotify(trackSaveEvent);
      if (saveResult.success) {
        return { success: true };
      }
      const isServerError = saveResult?.error?.isServerError ?? false;

      if (isServerError) {
        return {
          success: false,
          error: { errorMessage: teamAccessErrorMessage },
        };
      }

      const errorMessage = saveResult.error?.errorMessage;
      return { success: false, error: { errorMessage } };
    };

  const handleOnClickHeaderPoint = (
    fromStepIndex: number,
    toStepIndex: number,
  ) =>
    trackEvent(
      `notifications-nav-from-step-${fromStepIndex}-to-${toStepIndex}`,
      {},
    );

  const handleCancel = () => {
    trackEvent(EVENT_NAME_MANAGE_NOTIFICATIONS_TEAM_ACCESS_CANCEL_PRESSED, {});
    onCancel();
  };

  const handleOnClickGoNext = (): StepActionResult => {
    trackEvent(EVENT_NAME_MANAGE_NOTIFICATIONS_TEAM_ACCESS_NEXT_PRESSED, {});
    return { success: true };
  };

  const handleOnClickGoBack = () =>
    trackEvent(EVENT_NAME_MANAGE_NOTIFICATIONS_TEAM_ACCESS_BACK_PRESSED, {});

  const onNotifyChange = (eventData: OnNotifyChangeEventData) =>
    setNotifyTeam(eventData.teamMemberNotifySelected);

  const sendEmailButtonLabel = t('Send');
  const stepNames = [t('Select team')];
  if (notifyTeam) {
    stepNames.push(t('Email team'));
  }

  return (
    <Box height="full">
      <Stepper
        onComplete={onComplete}
        stepNames={stepNames}
        onClickHeaderPoint={handleOnClickHeaderPoint}
      >
        <Step
          onCancel={handleCancel}
          onClickNextAsync={
            notifyTeam ? undefined : handleSaveHirerTeamMemberJobAccessAndNotify
          }
          onClickNext={notifyTeam ? handleOnClickGoNext : undefined}
          footerNotification={
            <AddTeamMembersCallout teamMembers={teamMembers} />
          }
        >
          <HirerTeamMemberJobAccessForm
            id="team-members"
            onNotifyChange={onNotifyChange}
          />
        </Step>
        <Step
          saveButtonLabel={sendEmailButtonLabel}
          onClickNextAsync={handleSaveHirerTeamMemberJobAccessAndNotify}
          onClickGoBack={handleOnClickGoBack}
        >
          <TeamMemberEmail
            jobId={jobId}
            jobTitle={jobTitle}
            userName={userName}
          />
        </Step>
      </Stepper>
    </Box>
  );
};
